<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMediaQuery } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import WidgetCard from './WidgetCard.vue';
import WidgetDialog from './WidgetDialog.vue';
import WidgetResizeHandles from './WidgetResizeHandles.vue';
import { useCommercialWidgets } from './useCommercialWidgets';
import { useWidgetLayoutDrag } from './useWidgetLayoutDrag';
import {
  cellToRowCol,
  freeCells,
  GRID_COLS_CLASSES,
  COL_START_CLASSES,
  COL_SPAN_CLASSES,
  ROW_START_CLASSES,
  ROW_SPAN_CLASSES,
} from './widgetGrid';

defineProps({
  params: { type: Object, required: true },
});

const { t } = useI18n();
const {
  widgets,
  slots,
  columns,
  isLoading,
  fetchWidgets,
  removeWidget,
  reorder,
} = useCommercialWidgets();

const hasError = ref(false);
const gridRef = ref(null);
// Arrastar e redimensionar só de `lg` para cima (4 colunas); abaixo, a grade ignora larguras e alturas.
const isLargeScreen = useMediaQuery('(min-width: 1024px)');
// Muda a cada gravação para recarregar os cards (uma Taxa depende de outros widgets).
const reloadKey = ref(0);
const isDialogOpen = ref(false);
const editingWidget = ref(null);
const newWidgetPosition = ref(undefined);
const widgetToDelete = ref(null);
const deleteDialogRef = ref(null);
const isDeleting = ref(false);

const loadWidgets = async () => {
  hasError.value = false;
  try {
    await fetchWidgets();
  } catch {
    hasError.value = true;
  }
};

onMounted(loadWidgets);

const rows = computed(() => slots.value / columns.value);
const hasWidgets = computed(() => widgets.value.length > 0);
const vacancies = computed(() =>
  freeCells(widgets.value, columns.value, rows.value)
);
const hasVacancy = computed(() => vacancies.value.length > 0);

// Widgets e células livres na ordem das posições (é a ordem que vale abaixo de `lg`).
const items = computed(() =>
  [
    ...widgets.value.map(widget => ({ key: widget.id, widget })),
    ...vacancies.value.map(position => ({
      key: `free-${position}`,
      free: { position, width: 1, height: 1 },
    })),
  ].sort(
    (a, b) => (a.widget || a.free).position - (b.widget || b.free).position
  )
);

const placementClasses = ({ position, width, height }) => {
  const { row, col } = cellToRowCol(position, columns.value);
  return [
    COL_START_CLASSES[col + 1],
    COL_SPAN_CLASSES[width],
    ROW_START_CLASSES[row + 1],
    ROW_SPAN_CLASSES[height],
  ];
};

const saveLayout = async positions => {
  try {
    await reorder(positions);
  } catch (error) {
    useAlert(
      t(
        error.response?.status === 422
          ? 'COMMERCIAL_WIDGETS.TOASTS.LAYOUT_INVALID'
          : 'COMMERCIAL_WIDGETS.TOASTS.LAYOUT_ERROR'
      )
    );
  }
};

const { preview, movingId, isDragging, startMove, startResize } =
  useWidgetLayoutDrag({
    gridRef,
    widgets,
    columns,
    rows,
    onCommit: saveLayout,
    onReject: () => useAlert(t('COMMERCIAL_WIDGETS.TOASTS.LAYOUT_INVALID')),
  });

const onCellPointerDown = (event, widget) => {
  if (
    isLargeScreen.value &&
    event.button === 0 &&
    event.target.closest('.widget-drag-handle')
  ) {
    startMove(event, widget);
  }
};

const openDialog = (widget, position) => {
  editingWidget.value = widget || null;
  newWidgetPosition.value = position;
  isDialogOpen.value = true;
};

const onSaved = async () => {
  isDialogOpen.value = false;
  await loadWidgets();
  reloadKey.value += 1;
};

const askDelete = widget => {
  widgetToDelete.value = widget;
  deleteDialogRef.value.open();
};

const confirmDelete = async () => {
  isDeleting.value = true;
  try {
    await removeWidget(widgetToDelete.value.id);
    deleteDialogRef.value.close();
    useAlert(t('COMMERCIAL_WIDGETS.TOASTS.DELETED'));
  } catch (error) {
    const { data } = error.response || {};
    useAlert(
      data?.error === 'in_use_by_rates'
        ? t('COMMERCIAL_WIDGETS.TOASTS.IN_USE_BY_RATES', {
            rates: data.rates.map(rate => rate.title).join(', '),
          })
        : t('COMMERCIAL_WIDGETS.TOASTS.DELETE_ERROR')
    );
    deleteDialogRef.value.close();
  } finally {
    isDeleting.value = false;
  }
};
</script>

<template>
  <section class="border rounded-lg border-n-weak">
    <div class="flex items-center justify-between gap-2 p-3">
      <h2 class="m-0 font-medium text-n-slate-12">
        {{ t('COMMERCIAL_WIDGETS.TITLE') }}
      </h2>
      <Button
        v-if="hasWidgets && hasVacancy"
        data-pdf-ignore
        sm
        faded
        slate
        icon="i-lucide-plus"
        :label="t('COMMERCIAL_WIDGETS.ADD')"
        @click="openDialog(null)"
      />
    </div>

    <p v-if="hasError" class="px-3 pb-3 text-sm text-n-ruby-9">
      {{ t('COMMERCIAL_WIDGETS.LOAD_ERROR') }}
      <button type="button" class="underline" @click="loadWidgets">
        {{ t('COMMERCIAL_WIDGETS.RETRY') }}
      </button>
    </p>
    <p
      v-else-if="isLoading && !hasWidgets"
      class="px-3 pb-3 text-sm text-n-slate-11"
    >
      {{ t('COMMERCIAL_WIDGETS.LOADING') }}
    </p>
    <div
      v-else-if="!hasWidgets"
      data-pdf-ignore
      class="flex flex-col items-center gap-2 p-6 mx-3 mb-3 text-center border border-dashed rounded-lg border-n-strong"
    >
      <h3 class="m-0 font-medium text-n-slate-12">
        {{ t('COMMERCIAL_WIDGETS.EMPTY.TITLE') }}
      </h3>
      <p class="m-0 text-sm text-n-slate-11">
        {{ t('COMMERCIAL_WIDGETS.EMPTY.DESCRIPTION') }}
      </p>
      <Button
        sm
        icon="i-lucide-plus"
        :label="t('COMMERCIAL_WIDGETS.ADD')"
        @click="openDialog(null)"
      />
    </div>
    <div v-else class="px-3 pb-3">
      <div
        ref="gridRef"
        :key="reloadKey"
        class="relative grid grid-cols-1 gap-3 sm:grid-cols-2 auto-rows-[13rem] [&_.widget-drag-handle]:touch-none"
        :class="[GRID_COLS_CLASSES[columns], { 'select-none': isDragging }]"
      >
        <template v-for="item in items" :key="item.key">
          <button
            v-if="item.free"
            type="button"
            data-pdf-ignore
            data-free-cell
            class="items-center justify-center hidden gap-1 text-sm border border-dashed rounded-lg lg:flex min-w-0 border-n-strong text-n-slate-11 hover:bg-n-alpha-1"
            :class="placementClasses(item.free)"
            @click="openDialog(null, item.free.position)"
          >
            <span class="i-lucide-plus size-4" />
            {{ t('COMMERCIAL_WIDGETS.ADD') }}
          </button>
          <div
            v-else
            class="relative min-w-0 group"
            :class="[
              placementClasses(item.widget),
              { 'opacity-50': movingId === item.widget.id },
            ]"
            :data-widget-id="item.widget.id"
            @pointerdown="onCellPointerDown($event, item.widget)"
          >
            <WidgetCard
              :widget="item.widget"
              :params="params"
              @edit="openDialog(item.widget)"
              @delete="askDelete(item.widget)"
            />
            <WidgetResizeHandles
              v-if="isLargeScreen"
              @start="
                (direction, event) => startResize(event, item.widget, direction)
              "
            />
          </div>
        </template>
        <div
          v-if="preview"
          data-pdf-ignore
          data-layout-preview
          class="z-10 border-2 rounded-lg pointer-events-none"
          :class="[
            placementClasses(preview.rect),
            preview.valid ? 'border-n-teal-9' : 'border-n-ruby-9',
          ]"
        />
      </div>
    </div>

    <WidgetDialog
      v-if="isDialogOpen"
      :widget="editingWidget"
      :widgets="widgets"
      :position="newWidgetPosition"
      @saved="onSaved"
      @close="isDialogOpen = false"
    />

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('COMMERCIAL_WIDGETS.DELETE_DIALOG.TITLE')"
      :description="
        t('COMMERCIAL_WIDGETS.DELETE_DIALOG.DESCRIPTION', {
          title: widgetToDelete?.title,
        })
      "
      :confirm-button-label="t('COMMERCIAL_WIDGETS.DELETE_DIALOG.CONFIRM')"
      :is-loading="isDeleting"
      @confirm="confirmDelete"
    />
  </section>
</template>
